import { type JSX, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  ManageSubscriptionDialog,
  SubscriptionPlanIcon,
} from "features/subscriptions/components";
import useSubscriptions from "features/subscriptions/hooks/useSubscriptions";

import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import { Tag } from "common/components/ui/Tag";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { PLAN_SETTINGS_BACKGROUNDS } from "../constants";
import ScheduledChangeTag from "./AvatarPlan/ScheduledChangeTag";
import CancelSubscriptionDialog from "./CancelSubscriptionDialog";
import NonRenewingTag from "./NonRenewingTag";
import { PlanSettingsCard, PlanSettingsLeftPanel } from "./PlanSettingsCard";
import RemoveChangeButton from "./RemoveChangeButton";
import SubscriptionBillingPanel from "./SubscriptionBillingPanel";

interface AgencySubscriptionCardProps {
  isAgencyPage: boolean;
  onManageSubscription?: () => void;
}

const PLAN_FEATURES = [
  "Slack onboarding",
  "Team collaboration",
  "Full whitelabel",
  "Profile migration",
];

export default function AgencySubscriptionCard({
  isAgencyPage,
  onManageSubscription,
}: AgencySubscriptionCardProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);

  const { role, agency_subscription_status } = useSelectedWorkspaceContext();
  const { agency } = useSubscriptions();

  const shouldDisableRemoveChangeRef = useRef(false);

  const buttonSize = isDesktop ? "sm" : "md";

  let tagComponent = <Tag variant="disabled">Inactive</Tag>;

  if (agency_subscription_status === "non_renewing") {
    tagComponent = <NonRenewingTag type="agency" />;
  } else if (agency_subscription_status === "active") {
    tagComponent = <Tag variant="success">Active</Tag>;
  }

  const hasChanges = agency?.subscription.has_changes ?? false;

  if (agency && hasChanges) {
    tagComponent = <ScheduledChangeTag type="agency" />;
  }

  let buttonComponent: JSX.Element;

  if (isAgencyPage) {
    if (agency_subscription_status === "non_renewing") {
      // TODO: Replace null with RemoveChangeButton
      // <RemoveChangeButton subscription={subscription} type="cancellation" isInitiallyDisabled={shouldDisableRemoveChangeRef.current} />
      buttonComponent = null;
    } else if (hasChanges) {
      buttonComponent = (
        <RemoveChangeButton
          subscription={agency.subscription}
          type="change"
          isInitiallyDisabled={shouldDisableRemoveChangeRef.current}
        />
      );
    } else {
      buttonComponent = (
        <Button
          size={buttonSize}
          onClick={onManageSubscription ?? (() => setIsManageOpen(true))}
          /*dropdownMenuItems={[
            {
              label: "Cancel subscription",
              icon: Trash,
              onSelect: () => setIsCancelOpen(true),
              variant: "danger",
            },
          ]}*/
        >
          Manage subscription
        </Button>
      );
    }
  } else if (role === "owner") {
    buttonComponent =
      agency_subscription_status === "cancelled" ? (
        <Button asChild size={buttonSize} className="ml-auto">
          <Link to="../agency">Reactivate subscription</Link>
        </Button>
      ) : (
        <RestrictedComponent visibleFor={["billing_full"]}>
          <Button
            size={buttonSize}
            className="ml-auto max-lg:mt-9"
            onClick={() => setIsManageOpen(true)}
          >
            Manage workspace seats
          </Button>
        </RestrictedComponent>
      );
  }

  return (
    <>
      {isAgencyPage ? (
        <>
          <PlanSettingsCard
            backgroundClassName={PLAN_SETTINGS_BACKGROUNDS.agency}
            left={
              <PlanSettingsLeftPanel
                icon={<SubscriptionPlanIcon plan="agency" size="sm" />}
                title="Agency plan"
                tags={tagComponent}
                features={PLAN_FEATURES}
                action={buttonComponent}
              />
            }
            right={
              <SubscriptionBillingPanel subscription={agency?.subscription} />
            }
          />

          <ManageSubscriptionDialog
            initialState={agency || "agency"}
            isOpen={isManageOpen}
            setIsOpen={setIsManageOpen}
            mixpanelSource="billing_page"
          />

          {agency && (
            <CancelSubscriptionDialog
              isOpen={isCancelOpen}
              setIsOpen={setIsCancelOpen}
              shouldShowSurvey={false}
              subscriptionId={agency.subscription.id}
              onCancellationSuccess={() => {
                shouldDisableRemoveChangeRef.current = true;
              }}
            />
          )}
        </>
      ) : (
        <section className="p-6 flex flex-col justify-between gap-3 lg:gap-6 bg-white bg-[url(/images/plans/settings/agency-bg.webp)] bg-cover bg-center rounded-3xl border border-black-200">
          <div className="flex flex-row gap-2 items-center">
            <SubscriptionPlanIcon plan="agency" size="sm" />
            <span className="text-lg-bold">Outreach plan</span>
          </div>

          <div className="flex flex-row gap-4 items-center justify-between">
            <span className="text-sm-medium">
              This workspace uses Outreach seats from Agency plan.
              <RenderIf condition={role !== "owner"}>
                <br />
                Contact your admin to manage seats.
              </RenderIf>
            </span>

            <RenderIf condition={role === "owner"}>
              <Button asChild size="sm">
                <Link to="../agency">Go to Agency billing</Link>
              </Button>
            </RenderIf>
          </div>
        </section>
      )}
    </>
  );
}
